import type { Locale } from '../schemas/common.js';

import enBcIntegration from './en/bcIntegration.json' with { type: 'json' };
import enClaims from './en/claims.json' with { type: 'json' };
import enCommon from './en/common.json' with { type: 'json' };
import enDevicesPrinting from './en/devicesPrinting.json' with { type: 'json' };
import enMasterData from './en/masterData.json' with { type: 'json' };
import enPickingShipping from './en/pickingShipping.json' with { type: 'json' };
import enProduction from './en/production.json' with { type: 'json' };
import enReceiving from './en/receiving.json' with { type: 'json' };
import enReplenishment from './en/replenishment.json' with { type: 'json' };
import enSalesOrders from './en/salesOrders.json' with { type: 'json' };
import enWarehouseCore from './en/warehouseCore.json' with { type: 'json' };

import svBcIntegration from './sv/bcIntegration.json' with { type: 'json' };
import svClaims from './sv/claims.json' with { type: 'json' };
import svCommon from './sv/common.json' with { type: 'json' };
import svDevicesPrinting from './sv/devicesPrinting.json' with { type: 'json' };
import svMasterData from './sv/masterData.json' with { type: 'json' };
import svPickingShipping from './sv/pickingShipping.json' with { type: 'json' };
import svProduction from './sv/production.json' with { type: 'json' };
import svReceiving from './sv/receiving.json' with { type: 'json' };
import svReplenishment from './sv/replenishment.json' with { type: 'json' };
import svSalesOrders from './sv/salesOrders.json' with { type: 'json' };
import svWarehouseCore from './sv/warehouseCore.json' with { type: 'json' };

import zhBcIntegration from './zh/bcIntegration.json' with { type: 'json' };
import zhClaims from './zh/claims.json' with { type: 'json' };
import zhCommon from './zh/common.json' with { type: 'json' };
import zhDevicesPrinting from './zh/devicesPrinting.json' with { type: 'json' };
import zhMasterData from './zh/masterData.json' with { type: 'json' };
import zhPickingShipping from './zh/pickingShipping.json' with { type: 'json' };
import zhProduction from './zh/production.json' with { type: 'json' };
import zhReceiving from './zh/receiving.json' with { type: 'json' };
import zhReplenishment from './zh/replenishment.json' with { type: 'json' };
import zhSalesOrders from './zh/salesOrders.json' with { type: 'json' };
import zhWarehouseCore from './zh/warehouseCore.json' with { type: 'json' };

export const defaultLocale: Locale = 'sv';
export const locales = ['sv', 'en', 'zh'] as const;

/**
 * `sv` is the source-of-truth catalog shape — Swedish is the operating language
 * (labels and printouts stay Swedish per ARCHITECTURE §9). The `satisfies`
 * clauses below make `en` and `zh` a COMPILE ERROR the moment they drift from
 * the Swedish key set: add a key to sv/x.json and typecheck fails until en/zh
 * have it too. Same pattern as chopchop-lms.
 *
 * Module ownership: an agent owns `{sv,en,zh}/<its-namespace>.json` and never
 * touches this index — every namespace slot is pre-wired here by the scaffold.
 */
export type CommonCatalog = typeof svCommon;
export type MasterDataCatalog = typeof svMasterData;
export type WarehouseCoreCatalog = typeof svWarehouseCore;
export type ReceivingCatalog = typeof svReceiving;
export type SalesOrdersCatalog = typeof svSalesOrders;
export type PickingShippingCatalog = typeof svPickingShipping;
export type ProductionCatalog = typeof svProduction;
export type ClaimsCatalog = typeof svClaims;
export type BcIntegrationCatalog = typeof svBcIntegration;
export type DevicesPrintingCatalog = typeof svDevicesPrinting;
export type ReplenishmentCatalog = typeof svReplenishment;

export interface Catalogs {
  common: CommonCatalog;
  masterData: MasterDataCatalog;
  warehouseCore: WarehouseCoreCatalog;
  receiving: ReceivingCatalog;
  salesOrders: SalesOrdersCatalog;
  pickingShipping: PickingShippingCatalog;
  production: ProductionCatalog;
  claims: ClaimsCatalog;
  bcIntegration: BcIntegrationCatalog;
  devicesPrinting: DevicesPrintingCatalog;
  replenishment: ReplenishmentCatalog;
}

/** i18next namespaces, in load order. */
export const namespaces = [
  'common',
  'masterData',
  'warehouseCore',
  'receiving',
  'salesOrders',
  'pickingShipping',
  'production',
  'claims',
  'bcIntegration',
  'devicesPrinting',
  'replenishment',
] as const;
export type Namespace = (typeof namespaces)[number];
export const defaultNamespace: Namespace = 'common';

export const resources = {
  sv: {
    common: svCommon,
    masterData: svMasterData,
    warehouseCore: svWarehouseCore,
    receiving: svReceiving,
    salesOrders: svSalesOrders,
    pickingShipping: svPickingShipping,
    production: svProduction,
    claims: svClaims,
    bcIntegration: svBcIntegration,
    devicesPrinting: svDevicesPrinting,
    replenishment: svReplenishment,
  },
  en: {
    common: enCommon satisfies CommonCatalog,
    masterData: enMasterData satisfies MasterDataCatalog,
    warehouseCore: enWarehouseCore satisfies WarehouseCoreCatalog,
    receiving: enReceiving satisfies ReceivingCatalog,
    salesOrders: enSalesOrders satisfies SalesOrdersCatalog,
    pickingShipping: enPickingShipping satisfies PickingShippingCatalog,
    production: enProduction satisfies ProductionCatalog,
    claims: enClaims satisfies ClaimsCatalog,
    bcIntegration: enBcIntegration satisfies BcIntegrationCatalog,
    devicesPrinting: enDevicesPrinting satisfies DevicesPrintingCatalog,
    replenishment: enReplenishment satisfies ReplenishmentCatalog,
  },
  zh: {
    common: zhCommon satisfies CommonCatalog,
    masterData: zhMasterData satisfies MasterDataCatalog,
    warehouseCore: zhWarehouseCore satisfies WarehouseCoreCatalog,
    receiving: zhReceiving satisfies ReceivingCatalog,
    salesOrders: zhSalesOrders satisfies SalesOrdersCatalog,
    pickingShipping: zhPickingShipping satisfies PickingShippingCatalog,
    production: zhProduction satisfies ProductionCatalog,
    claims: zhClaims satisfies ClaimsCatalog,
    bcIntegration: zhBcIntegration satisfies BcIntegrationCatalog,
    devicesPrinting: zhDevicesPrinting satisfies DevicesPrintingCatalog,
    replenishment: zhReplenishment satisfies ReplenishmentCatalog,
  },
} as const satisfies Record<Locale, Catalogs>;

/** Server-side helper for localized email/notification copy (`users.language`). */
export function catalogFor(locale: Locale): Catalogs {
  return resources[locale];
}
